import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, CheckCircle, Download, Filter, RefreshCw, Save, Search, Settings, Trash2, XCircle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { alertService } from '../services/api';
import { useAlertStore } from '../store';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { exportCsv } from '../utils/csv';

const alertTypes = {
  all: 'Todos os tipos',
  speed_limit: 'Excesso de velocidade',
  geofence_enter: 'Entrada em cerca',
  geofence_exit: 'Saída de cerca',
  ignition_on: 'Ignição ligada',
  ignition_off: 'Ignição desligada',
  low_battery: 'Bateria baixa',
  device_offline: 'Dispositivo offline',
  sos: 'SOS',
  vibration: 'Vibração',
  custom: 'Personalizado',
};

const severityLabels = {
  all: 'Todas as severidades',
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
};

const severityClass = {
  low: 'badge-info',
  medium: 'badge-warning',
  high: 'badge-warning',
  critical: 'badge-danger',
};

const defaultAlertSettings = {
  speed_limit: {
    enabled: false,
    threshold: 80,
    cooldown_minutes: 5,
    severity: 'high',
  },
  low_battery: {
    enabled: false,
    threshold: 20,
    cooldown_minutes: 60,
    severity: 'medium',
  },
  ignition_on: {
    enabled: false,
    cooldown_minutes: 2,
    severity: 'low',
  },
  ignition_off: {
    enabled: false,
    cooldown_minutes: 2,
    severity: 'low',
  },
  movement_started: {
    enabled: false,
    cooldown_minutes: 10,
    severity: 'medium',
  },
};

const defaultNotificationSettings = {
  email_enabled: false,
  notification_emails: '',
};

const mergeAlertSettings = (settings = {}) => ({
  ...Object.entries(defaultAlertSettings).reduce((result, [key, value]) => ({
    ...result,
    [key]: {
      ...value,
      ...(settings?.[key] || {}),
    },
  }), {}),
  email_enabled: Boolean(settings.email_enabled ?? defaultNotificationSettings.email_enabled),
  notification_emails: settings.notification_emails || defaultNotificationSettings.notification_emails,
});

const Alerts = () => {
  const {
    alerts,
    setAlerts,
    markAsRead,
    markAllAsRead,
    removeAlert,
    setUnreadCount,
    loading,
    setLoading,
  } = useAlertStore();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [settingsVehicles, setSettingsVehicles] = useState([]);
  const [selectedSettingsVehicleId, setSelectedSettingsVehicleId] = useState('');
  const [settingsForm, setSettingsForm] = useState(defaultAlertSettings);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadAlerts();
    loadAlertSettings();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);

    try {
      const response = await alertService.getAll({ limit: 100 });
      setAlerts(response.data.data);

      const countResponse = await alertService.getUnreadCount();
      setUnreadCount(countResponse.data.data.count);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const loadAlertSettings = async () => {
    try {
      const response = await alertService.getSettings();
      const vehicles = response.data.data || [];
      setSettingsVehicles(vehicles);

      if (vehicles.length > 0) {
        const currentVehicle = vehicles.find((vehicle) => vehicle.id === selectedSettingsVehicleId) || vehicles[0];
        setSelectedSettingsVehicleId(currentVehicle.id);
        setSettingsForm(mergeAlertSettings(currentVehicle.alert_settings));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar regras de alertas');
    }
  };

  const filteredAlerts = useMemo(() => {
    const term = search.trim().toLowerCase();

    return alerts.filter((alert) => {
      const matchesSearch = !term || [
        alert.title,
        alert.message,
        alert.address,
        alert.vehicle?.name,
        alert.vehicle?.plate,
      ].filter(Boolean).some((value) => value.toLowerCase().includes(term));

      const matchesType = type === 'all' || alert.type === type;
      const matchesSeverity = severity === 'all' || alert.severity === severity;
      const matchesRead = readFilter === 'all' ||
        (readFilter === 'unread' && !alert.is_read) ||
        (readFilter === 'read' && alert.is_read);

      return matchesSearch && matchesType && matchesSeverity && matchesRead;
    });
  }, [alerts, readFilter, search, severity, type]);

  const selectedSettingsVehicle = useMemo(() => (
    settingsVehicles.find((vehicle) => vehicle.id === selectedSettingsVehicleId) || null
  ), [selectedSettingsVehicleId, settingsVehicles]);

  const unreadCount = alerts.filter((alert) => !alert.is_read).length;

  const getIcon = (alert) => {
    if (alert.severity === 'critical' || alert.type === 'sos') {
      return <AlertTriangle className="h-5 w-5 text-danger-400" />;
    }

    if (alert.is_read) {
      return <CheckCircle className="h-5 w-5 text-success-400" />;
    }

    return <Bell className="h-5 w-5 text-primary-400" />;
  };

  const handleMarkAsRead = async (alert) => {
    if (alert.is_read) return;

    try {
      await alertService.markAsRead(alert.id);
      markAsRead(alert.id);
      toast.success('Alerta marcado como lido');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao marcar alerta');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await alertService.markAllAsRead();
      markAllAsRead();
      toast.success('Todos os alertas foram marcados como lidos');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao marcar alertas');
    }
  };

  const handleDelete = async (alert) => {
    const confirmed = window.confirm(`Remover o alerta "${alert.title}"?`);
    if (!confirmed) return;

    try {
      await alertService.delete(alert.id);
      removeAlert(alert.id);
      toast.success('Alerta removido');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover alerta');
    }
  };

  const handleSelectSettingsVehicle = (vehicleId) => {
    const vehicle = settingsVehicles.find((item) => item.id === vehicleId);
    setSelectedSettingsVehicleId(vehicleId);
    setSettingsForm(mergeAlertSettings(vehicle?.alert_settings));
  };

  const updateRule = (ruleName, values) => {
    setSettingsForm((current) => ({
      ...current,
      [ruleName]: {
        ...defaultAlertSettings[ruleName],
        ...(current[ruleName] || {}),
        ...values,
      },
    }));
  };

  const handleSaveSettings = async () => {
    if (!selectedSettingsVehicleId) return;

    setSavingSettings(true);

    try {
      const response = await alertService.updateSettings(selectedSettingsVehicleId, settingsForm);
      const updatedVehicle = response.data.data;
      const updatedSettings = mergeAlertSettings(updatedVehicle.alert_settings);

      setSettingsVehicles((current) => current.map((vehicle) => (
        vehicle.id === updatedVehicle.id
          ? { ...vehicle, alert_settings: updatedSettings }
          : vehicle
      )));
      setSettingsForm(updatedSettings);
      toast.success('Regras de alertas salvas');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar regras');
    } finally {
      setSavingSettings(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setType('all');
    setSeverity('all');
    setReadFilter('all');
  };

  const handleExport = () => {
    exportCsv('rcrastro-alertas.csv', [
      { key: 'title', label: 'Titulo' },
      { key: 'message', label: 'Mensagem' },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'plate', label: 'Placa' },
      { key: 'type', label: 'Tipo' },
      { key: 'severity', label: 'Severidade' },
      { key: 'read', label: 'Lido' },
      { key: 'address', label: 'Endereco' },
      { key: 'created_at', label: 'Criado em' },
    ], filteredAlerts.map((alert) => ({
      title: alert.title,
      message: alert.message,
      vehicle: alert.vehicle?.name || '',
      plate: alert.vehicle?.plate || '',
      type: alertTypes[alert.type] || alert.type,
      severity: severityLabels[alert.severity] || alert.severity,
      read: alert.is_read ? 'Sim' : 'Nao',
      address: alert.address || '',
      created_at: alert.created_at ? format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm') : '',
    })));
    toast.success('CSV de alertas gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Alertas</h1>
              <p className="text-dark-400">
                Acompanhe eventos da frota e trate notificações pendentes.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadAlerts}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0}
                className="btn-primary flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Marcar todos
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card">
              <p className="text-sm text-dark-400">Total</p>
              <p className="mt-2 text-3xl font-bold text-white">{alerts.length}</p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Não lidos</p>
              <p className="mt-2 text-3xl font-bold text-warning-400">{unreadCount}</p>
            </div>
            <div className="card">
              <p className="text-sm text-dark-400">Críticos</p>
              <p className="mt-2 text-3xl font-bold text-danger-400">
                {alerts.filter((alert) => alert.severity === 'critical').length}
              </p>
            </div>
          </div>

          {settingsVehicles.length > 0 && (
            <div className="card mb-6">
              <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Settings className="h-5 w-5 text-primary-400" />
                  <h2 className="font-semibold">Regras automaticas</h2>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <select
                    value={selectedSettingsVehicleId}
                    onChange={(event) => handleSelectSettingsVehicle(event.target.value)}
                    className="input-field min-w-[240px]"
                  >
                    {settingsVehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} ({vehicle.plate})
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={handleSaveSettings}
                    disabled={savingSettings || !selectedSettingsVehicle}
                    className="btn-primary flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {savingSettings ? 'Salvando...' : 'Salvar regras'}
                  </button>
                </div>
              </div>

              <div className="mb-4 rounded-lg border border-dark-700 bg-dark-900/60 p-4">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
                  <label className="flex items-center gap-2 text-sm font-medium text-dark-200">
                    <input
                      type="checkbox"
                      checked={settingsForm.email_enabled}
                      onChange={(event) => setSettingsForm((current) => ({
                        ...current,
                        email_enabled: event.target.checked,
                      }))}
                      className="h-4 w-4 accent-primary-500"
                    />
                    Enviar e-mail ao cliente
                  </label>

                  <label className="text-sm text-dark-300">
                    E-mails do cliente
                    <input
                      type="text"
                      value={settingsForm.notification_emails}
                      onChange={(event) => setSettingsForm((current) => ({
                        ...current,
                        notification_emails: event.target.value,
                      }))}
                      className="input-field mt-1"
                      placeholder="cliente@email.com, outro@email.com"
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-dark-700 bg-dark-900/60 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">Velocidade</h3>
                      <p className="text-sm text-dark-400">Excesso de velocidade</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-dark-200">
                      <input
                        type="checkbox"
                        checked={settingsForm.speed_limit.enabled}
                        onChange={(event) => updateRule('speed_limit', { enabled: event.target.checked })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="text-sm text-dark-300">
                      Limite km/h
                      <input
                        type="number"
                        min="5"
                        max="240"
                        value={settingsForm.speed_limit.threshold}
                        onChange={(event) => updateRule('speed_limit', { threshold: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Intervalo min
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={settingsForm.speed_limit.cooldown_minutes}
                        onChange={(event) => updateRule('speed_limit', { cooldown_minutes: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Severidade
                      <select
                        value={settingsForm.speed_limit.severity}
                        onChange={(event) => updateRule('speed_limit', { severity: event.target.value })}
                        className="input-field mt-1"
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Critica</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 bg-dark-900/60 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">Bateria</h3>
                      <p className="text-sm text-dark-400">Bateria baixa</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-dark-200">
                      <input
                        type="checkbox"
                        checked={settingsForm.low_battery.enabled}
                        onChange={(event) => updateRule('low_battery', { enabled: event.target.checked })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="text-sm text-dark-300">
                      Limite %
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={settingsForm.low_battery.threshold}
                        onChange={(event) => updateRule('low_battery', { threshold: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Intervalo min
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={settingsForm.low_battery.cooldown_minutes}
                        onChange={(event) => updateRule('low_battery', { cooldown_minutes: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Severidade
                      <select
                        value={settingsForm.low_battery.severity}
                        onChange={(event) => updateRule('low_battery', { severity: event.target.value })}
                        className="input-field mt-1"
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Critica</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 bg-dark-900/60 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">Ignicao ligada</h3>
                      <p className="text-sm text-dark-400">Mudanca de estado</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-dark-200">
                      <input
                        type="checkbox"
                        checked={settingsForm.ignition_on.enabled}
                        onChange={(event) => updateRule('ignition_on', { enabled: event.target.checked })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-sm text-dark-300">
                      Intervalo min
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={settingsForm.ignition_on.cooldown_minutes}
                        onChange={(event) => updateRule('ignition_on', { cooldown_minutes: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Severidade
                      <select
                        value={settingsForm.ignition_on.severity}
                        onChange={(event) => updateRule('ignition_on', { severity: event.target.value })}
                        className="input-field mt-1"
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Critica</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 bg-dark-900/60 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">Ignicao desligada</h3>
                      <p className="text-sm text-dark-400">Mudanca de estado</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-dark-200">
                      <input
                        type="checkbox"
                        checked={settingsForm.ignition_off.enabled}
                        onChange={(event) => updateRule('ignition_off', { enabled: event.target.checked })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-sm text-dark-300">
                      Intervalo min
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={settingsForm.ignition_off.cooldown_minutes}
                        onChange={(event) => updateRule('ignition_off', { cooldown_minutes: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Severidade
                      <select
                        value={settingsForm.ignition_off.severity}
                        onChange={(event) => updateRule('ignition_off', { severity: event.target.value })}
                        className="input-field mt-1"
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Critica</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 bg-dark-900/60 p-4 xl:col-span-2">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">Movimento detectado</h3>
                      <p className="text-sm text-dark-400">Quando o veiculo sair de parado para em movimento</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-dark-200">
                      <input
                        type="checkbox"
                        checked={settingsForm.movement_started.enabled}
                        onChange={(event) => updateRule('movement_started', { enabled: event.target.checked })}
                        className="h-4 w-4 accent-primary-500"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-sm text-dark-300">
                      Intervalo min
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        value={settingsForm.movement_started.cooldown_minutes}
                        onChange={(event) => updateRule('movement_started', { cooldown_minutes: event.target.value })}
                        className="input-field mt-1"
                      />
                    </label>
                    <label className="text-sm text-dark-300">
                      Severidade
                      <select
                        value={settingsForm.movement_started.severity}
                        onChange={(event) => updateRule('movement_started', { severity: event.target.value })}
                        className="input-field mt-1"
                      >
                        <option value="low">Baixa</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Critica</option>
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card mb-6">
            <div className="mb-4 flex items-center gap-2 text-white">
              <Filter className="h-5 w-5 text-primary-400" />
              <h2 className="font-semibold">Filtros</h2>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
              <div className="relative lg:col-span-2">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-field pl-11"
                  placeholder="Buscar por veículo, placa, texto ou endereço"
                />
              </div>

              <select value={type} onChange={(event) => setType(event.target.value)} className="input-field">
                {Object.entries(alertTypes).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="input-field">
                {Object.entries(severityLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <div className="flex gap-2">
                <select
                  value={readFilter}
                  onChange={(event) => setReadFilter(event.target.value)}
                  className="input-field"
                >
                  <option value="all">Todos</option>
                  <option value="unread">Não lidos</option>
                  <option value="read">Lidos</option>
                </select>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-lg border border-dark-600 px-3 text-dark-300 transition-colors hover:bg-dark-700 hover:text-white"
                  title="Limpar filtros"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="card py-10 text-center text-dark-400">Carregando alertas...</div>
            ) : filteredAlerts.length === 0 ? (
              <div className="card py-10 text-center text-dark-400">
                <Bell className="mx-auto mb-2 h-10 w-10 opacity-50" />
                Nenhum alerta encontrado
              </div>
            ) : (
              filteredAlerts.map((alert) => (
                <article
                  key={alert.id}
                  className={`card border-l-4 ${alert.is_read ? 'border-l-dark-600 opacity-75' : 'border-l-primary-500'}`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex gap-4">
                      <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-dark-700">
                        {getIcon(alert)}
                      </div>

                      <div>
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h2 className="text-lg font-semibold text-white">{alert.title}</h2>
                          <span className={`badge ${severityClass[alert.severity] || 'badge-info'}`}>
                            {severityLabels[alert.severity] || alert.severity}
                          </span>
                          {!alert.is_read && <span className="badge badge-info">Novo</span>}
                        </div>

                        <p className="text-sm text-dark-300">{alert.message}</p>

                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-dark-400">
                          {alert.vehicle && (
                            <span>
                              {alert.vehicle.name} ({alert.vehicle.plate})
                            </span>
                          )}
                          <span>{alertTypes[alert.type] || alert.type}</span>
                          {alert.created_at && (
                            <span>{format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm')}</span>
                          )}
                          {alert.address && <span>{alert.address}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      {!alert.is_read && (
                        <button
                          type="button"
                          onClick={() => handleMarkAsRead(alert)}
                          className="rounded-lg p-2 text-success-400 transition-colors hover:bg-dark-700"
                          title="Marcar como lido"
                        >
                          <CheckCircle className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(alert)}
                        className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                        title="Remover"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Alerts;
